import { ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';

// Shadcn Components
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";

// Icons
import { ArrowLeft } from "lucide-react";

// Hooks
import { useTenantNavigation } from '../../hooks/navigation/useTenantNavigation';
import { useAuth } from '../../hooks/authentication/useAuth';
import { Captcha } from '@/components/ui/captcha';

const HCAPTCHA_SITE_KEY = process.env.REACT_APP_HCAPTCHA_SITE_KEY || '';

const OrganizationLogin = () => {
  const navigate = useNavigate();
  const { navigateWithTenant } = useTenantNavigation();
  const {
    email, setEmail, password, setPassword, errors, handleSignIn,
    showCaptcha, setShowCaptcha, captchaToken, setCaptchaToken, captchaError, setCaptchaError
  } = useAuth();

  const gradientStyle = {
    background: "linear-gradient(45deg, #f804b4 0%, #08ccc4 100%)"
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center px-4 py-12" 
      style={gradientStyle}
    >
      <div className="w-full max-w-lg bg-white rounded-lg shadow-2xl overflow-hidden">
        <div className="px-8 py-6">
          <div className="flex flex-col items-center">
            <img 
              src="/branding/TriEvents_Loading_Big_Text.gif" 
              alt="TriEvents Logo" 
              className="w-40 sm:w-48 h-auto mb-2"
            />
            <h1 className="text-2xl font-semibold text-foreground">Organization Login</h1>
            <p className="text-muted-foreground text-center text-sm mt-0.5 mb-4">
              Login to your organization account
            </p>
          </div>

          <form onSubmit={handleSignIn} className="space-y-4">
            {errors.general && (
              <Alert variant="destructive" className="mb-2">
                <AlertDescription>{errors.general}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                type="email"
                id="email"
                name="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                required
                className={errors.email ? "border-destructive" : ""}
              />
              {errors.email && (
                <p className="text-sm text-destructive">{errors.email}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input
                type="password"
                id="password"
                name="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                required
                className={errors.password ? "border-destructive" : ""}
              />
              {errors.password && (
                <p className="text-sm text-destructive">{errors.password}</p>
              )}
            </div>

            {showCaptcha && (
              <div className="mb-2">
                <Captcha
                  siteKey={HCAPTCHA_SITE_KEY}
                  onVerify={setCaptchaToken}
                  onError={() => setCaptchaError('CAPTCHA verification failed')}
                />
                {captchaError && (
                  <Alert variant="destructive" className="mt-2">
                    <AlertDescription>{captchaError}</AlertDescription>
                  </Alert>
                )}
              </div>
            )}

            <div className="space-y-3">
              <Button 
                type="submit"
                size="lg"
                className="w-full transition-transform hover:scale-105"
              >
                Login
              </Button>

              <Button
                variant="ghost"
                onClick={() => navigateWithTenant('/auth/reset-password')}
                className="w-full transition-transform hover:scale-105"
              >
                Forgot password?
              </Button>
            </div>

            <div className="flex flex-col-reverse sm:flex-row items-center justify-between gap-4 pt-2">
              <Button
                variant="outline"
                onClick={() => navigate('/auth')}
                className="transform hover:scale-105 transition-all duration-200 w-full sm:w-auto"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <Button
                variant="outline"
                onClick={() => navigateWithTenant('/join')}
                className="transform hover:scale-105 transition-all duration-200 w-full sm:w-auto"
              >
                Join Organization
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default OrganizationLogin;
